import { useSyncExternalStore } from "react";
import type { GuardianDenial } from "@bw/shared/api";

/**
 * The one place a guardian-control 403 (GUARDIAN_REQUIRED / GUARDIAN_DECLINED /
 * GUARDIAN_APPROVAL_REQUIRED) lands, whichever screen made the request. The
 * query client's mutation cache publishes here; the app shell's GuardianPrompt
 * shows it. Kept outside React state so the query client can publish without
 * a component in hand.
 */
let current: GuardianDenial | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function publishGuardianDenial(denial: GuardianDenial): void {
  current = denial;
  emit();
}

export function clearGuardianDenial(): void {
  if (current === null) return;
  current = null;
  emit();
}

/** Listen for published/cleared denials (outside React; the hook uses it too). */
export function subscribeGuardianDenial(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function currentGuardianDenial(): GuardianDenial | null {
  return current;
}

export function useGuardianDenial(): GuardianDenial | null {
  return useSyncExternalStore(subscribeGuardianDenial, currentGuardianDenial, () => null);
}

/**
 * Mutation meta for a mutation that shows its own error area: the app shell's
 * GuardianPrompt stays quiet and the area renders the guardian answer through
 * `GuardianAwareError`, so the child sees one message, not a generic "That
 * didn't work" beside a guardian prompt.
 */
export const INLINE_GUARDIAN_PROMPT = { guardianPrompt: "inline" } as const;
